<template>
  <div :class="{'dashboard-loading': isLoading}">
    <template>
      <div class="dashboard-greeting">
        <h1>Hey {{partnerName ? partnerName : userName}}, Welcome to your Dashboard!</h1>
        <p v-if="hasContractor || partnerId !== null">
          Here you can manage your account, track progress and get support.
        </p>
      </div>
      <div v-if="newsAndUpdates.length > 0" class="dashboard-news-and-updates-notification">
        <div class="block notification-wrapper"
             v-for="item in newsAndUpdates" :key="item.id">
            <NotificationNewsAndUpdates title="News & Updates"
                                        @close="closeNotificationWithNews"
                                        :content="item.releaseNote" :id="item.id"/>
        </div>
      </div>
      <b-loading :is-full-page="false" :active="isLoading" :can-cancel="false"></b-loading>
      <div v-if="!isLoading && (hasContractor || hasPartner)" class="dashboard-widget-list">
        <WidgetCard v-if="coBrandedWidgetData" v-bind="coBrandedWidgetData">
          <WidgetCardCoBrandedPage
            v-bind="coBrandedWidgetData"
            :viewer="hasContractor ? 'customer' : 'partner'"
          />
        </WidgetCard>

        <WidgetCard
          v-if="hasContractor && recommendedTrainingWidgetData"
          v-bind="recommendedTrainingWidgetData"
        >
          <WidgetCardRecommendedTraining v-bind="recommendedTrainingWidgetData"/>
        </WidgetCard>

        <WidgetCard v-if="achWidgetData" v-bind="achWidgetData">
          <WidgetCardAchInfo v-bind="achWidgetData"/>
        </WidgetCard>

        <WidgetCard v-if="w9WidgetData" v-bind="w9WidgetData">
          <WidgetCardW9Form v-bind="w9WidgetData"/>
        </WidgetCard>

        <WidgetCard v-if="hasContractor && referWidgetData" v-bind="referWidgetData">
          <WidgetCardRefer v-bind="referWidgetData"/>
        </WidgetCard>

        <WidgetCard
          v-if="hasContractor && marketingMaterialsWidgetData"
          v-bind="marketingMaterialsWidgetData"
        >
          <WidgetCardMarketingMaterials v-bind="marketingMaterialsWidgetData"/>
        </WidgetCard>

        <WidgetCard v-if="hasContractor && paymentWidgetData" v-bind="paymentWidgetData">
          <WidgetCardWidgetCardPaymentInfo v-bind="paymentWidgetData"/>
        </WidgetCard>
      </div>
    </template>
  </div>
</template>

<script>
import WidgetCard from '@/views/Dashboard/WidgetCard.vue';
import WidgetCardCoBrandedPage from '@/views/Dashboard/WidgetCard/CoBrandedPage.vue';
import WidgetCardW9Form from '@/views/Dashboard/WidgetCard/W9Form.vue';
import WidgetCardAchInfo from '@/views/Dashboard/WidgetCard/AchInfo.vue';
import WidgetCardRefer from '@/views/Dashboard/WidgetCard/Refer.vue';
import WidgetCardMarketingMaterials from '@/views/Dashboard/WidgetCard/MarketingMaterials.vue';
import WidgetCardRecommendedTraining from '@/views/Dashboard/WidgetCard/RecommendedTraining.vue';
import WidgetCardWidgetCardPaymentInfo from '@/views/Dashboard/WidgetCard/PaymentInfo.vue';
import NotificationNewsAndUpdates from '@/components/NotificationNewsAndUpdates.vue';

import { mapActions, mapGetters, mapState } from 'vuex';
import { getUserCard } from '@/api/card_payments';
import { coBrandedAnalytics, contractorPayoutTax } from '@/api/contractor';
import { partnerPayoutTax } from '@/api/partner';
import * as CONTRACTOR_ACTIONS from '@/store/modules/contractor/actions';
import * as PARTNER_ACTIONS from '@/store/modules/partner/actions';
import widgetCardData from '@/views/Dashboard/WidgetCard/widgetCardData';
import { getUserNewsAndUpdates } from '@/api/user';
import { CO_BRANDED_PAGE_ACCESS, CO_BRANDED_PAGE_ACCESS_MANAGE } from '@/const/privileges';

const CARD_PAYMENTS_ACCESS = { resource: 'card-payments', code: 'access' };
const CARD_PAYMENTS_ACCESS_MANAGE = { resource: 'card-payments', code: 'manage_access' };
const PAYOUT_TAX_ACCESS = { resource: 'payout-tax', code: 'access' };
const PAYOUT_TAX_ACCESS_MANAGE = { resource: 'payout-tax', code: 'manage_access' };
const MARKETING_MATERIALS_ACCESS = { resource: 'marketing-materials', code: 'access' };
const MARKETING_MATERIALS_ACCESS_MANAGE = { resource: 'marketing-materials', code: 'manage_access' };
const REFER_ACCESS = { resource: 'refer', code: 'access' };
const REFER_ACCESS_MANAGE = { resource: 'refer', code: 'manage_access' };
const TRAINING_AND_FAQS_ACCESS = { resource: 'training-and-faqs', code: 'access' };
const TRAINING_AND_FAQS_ACCESS_MANAGE = { resource: 'training-and-faqs', code: 'manage_access' };
const REPORTS_APPLICATIONS_ACCESS = { resource: 'reports-applications', code: 'access' };
const REPORTS_APPLICATIONS_ACCESS_MANAGE = { resource: 'reports-applications', code: 'manage_access' };

export default {
  name: 'Dashboard',
  components: {
    WidgetCard,
    WidgetCardCoBrandedPage,
    WidgetCardAchInfo,
    WidgetCardW9Form,
    WidgetCardRefer,
    WidgetCardMarketingMaterials,
    WidgetCardRecommendedTraining,
    WidgetCardWidgetCardPaymentInfo,
    NotificationNewsAndUpdates,
  },
  data: () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 729);

    return {
      startDate,
      endDate,
      isLoading: true,
      coBrandedAnalytics: null,
      hasAchData: false,
      w9Status: null,
      hasBankCard: false,
      coBrandedWidgetIsLoading: true,
      paymentCard: {
        daysUntilExpiration: null,
        number: null,
      },
      newsAndUpdates: [],
    };
  },
  computed: {
    widgetCardData() {
      return { ...widgetCardData };
    },
    ...mapGetters(['hasAccess']),
    ...mapState('user', {
      userName: (state) => state.firstname,
      userId: (state) => state.id,
      hasContractor: (state) => state.contractor !== null,
      hasPartner: (state) => state.partnerId !== null,
    }),
    ...mapState('contractor', {
      contractorHasPage: (state) => state.hasPage,
      contractorId: (state) => state.id,
      contractorPages: (state) => state.contractorPages,
    }),
    ...mapState('partner', {
      partnerHasPage: (state) => state.hasPage,
      partnerName: (state) => state.firstName,
      partnerId: (state) => state.id,
    }),
    coBrandedWidgetData() {
      let card = null;

      if (this.hasAccess(CO_BRANDED_PAGE_ACCESS, CO_BRANDED_PAGE_ACCESS_MANAGE)) {
        if (this.contractorId) {
          if (this.contractorHasPage === true) {
            if (this.coBrandedWidgetIsLoading) {
              card = this.widgetCardData.coBrandedPage.loading;
            } else {
              card = this.makeCoBrandedWidgetCardForCustomerWithPage(card);
            }
          } else {
            card = this.widgetCardData.coBrandedPage['1'];
          }
        } else if (!this.partnerHasPage) {
          card = this.widgetCardData.coBrandedPage['1'];
        }
      }

      return card;
    },
    recommendedTrainingWidgetData() {
      if (!this.hasAccess(TRAINING_AND_FAQS_ACCESS, TRAINING_AND_FAQS_ACCESS_MANAGE)) {
        return null;
      }

      return this.widgetCardData.recommendedTraining['1'];
    },
    achWidgetData() {
      if (
        (!this.hasContractor && !this.hasPartner)
        || !this.hasAccess(PAYOUT_TAX_ACCESS, PAYOUT_TAX_ACCESS_MANAGE)
      ) {
        return null;
      }

      if (this.hasAchData !== true) {
        return this.widgetCardData.ach['1'];
      }
      return null;
    },
    w9WidgetData() {
      if (
        (!this.hasContractor && !this.hasPartner)
        || !this.hasAccess(PAYOUT_TAX_ACCESS, PAYOUT_TAX_ACCESS_MANAGE)
      ) {
        return null;
      }

      if (this.w9Status === null || this.w9Status === 'Rejected') {
        return this.widgetCardData.w9['1'];
      }
      return null;
    },
    referWidgetData() {
      if (!this.hasAccess(REFER_ACCESS, REFER_ACCESS_MANAGE)) {
        return null;
      }

      return this.widgetCardData.refer['1'];
    },
    marketingMaterialsWidgetData() {
      if (!this.hasAccess(MARKETING_MATERIALS_ACCESS, MARKETING_MATERIALS_ACCESS_MANAGE)) {
        return null;
      }

      return this.widgetCardData.marketingMaterials['1'];
    },
    paymentWidgetData() {
      if (!this.hasAccess(CARD_PAYMENTS_ACCESS, CARD_PAYMENTS_ACCESS_MANAGE)) {
        return null;
      }

      if (this.hasBankCard !== true) {
        return this.widgetCardData.payment['1'];
      }

      const { daysUntilExpiration } = this.paymentCard;
      if (daysUntilExpiration === null) {
        return null;
      }

      if (daysUntilExpiration <= 30 && daysUntilExpiration >= 1) {
        const card = this.widgetCardData.payment['2'];
        card.expireText = card.getExpireText(
          this.paymentCard.daysUntilExpiration,
          this.paymentCard.number,
        );
        return card;
      }

      if (daysUntilExpiration < 1) {
        const card = this.widgetCardData.payment['3'];
        card.warning.title = card.warning.getTitle(this.paymentCard.number);
        return card;
      }

      return null;
    },
  },
  activated() {
    this.loadAsyncData();
  },
  deactivated() {
    this.isLoading = true;
    this.coBrandedWidgetIsLoading = true;
  },
  methods: {
    ...mapActions('contractor', { updPages: CONTRACTOR_ACTIONS.LOAD_PAGES }),
    async loadAsyncData() {
      try {
        const { data } = await getUserNewsAndUpdates(this.userId);
        const storedNewsAndUpdates = JSON.parse(localStorage.getItem('newsAndUpdates') ?? '[]');
        if (storedNewsAndUpdates.length > 0) {
          this.newsAndUpdates = data.filter(
            (item) => storedNewsAndUpdates.findIndex(
              (val) => !val.closed && val.id === item.id,
            ) > -1,
          );
        } else {
          this.newsAndUpdates = data;
          const toLocalStorage = [];
          data.forEach((item) => {
            toLocalStorage.push({ id: item.id, closed: 0 });
          });
          localStorage.setItem('newsAndUpdates', JSON.stringify(toLocalStorage));
        }
        this.coBrandedWidgetIsLoading = true;
        if (this.hasContractor) {
          if (this.hasAccess(CARD_PAYMENTS_ACCESS, CARD_PAYMENTS_ACCESS_MANAGE)) {
            const userData = await getUserCard(this.userId);
            this.hasBankCard = Object.prototype.hasOwnProperty.call(userData, 'data')
                && Object.prototype.hasOwnProperty.call(userData.data, 'id');
            this.paymentCard.daysUntilExpiration = userData.data.daysUntilExpiration;
            this.paymentCard.number = userData.data.number;
          }

          if (this.hasAccess(PAYOUT_TAX_ACCESS, PAYOUT_TAX_ACCESS_MANAGE)) {
            const payoutTaxData = await contractorPayoutTax(this.contractorId);
            this.hasAchData = Object.keys(payoutTaxData.data).length > 0;

            await this.$store.dispatch(`contractor/${CONTRACTOR_ACTIONS.LOAD_W9}`);
            this.w9Status = this.$store.state.contractor.w9.status;
          }
        }

        if (this.hasPartner && this.hasAccess(PAYOUT_TAX_ACCESS, PAYOUT_TAX_ACCESS_MANAGE)) {
          const payoutTaxData = await partnerPayoutTax(this.partnerId);
          this.hasAchData = Object.keys(payoutTaxData.data).length > 0;

          await this.$store.dispatch(`partner/${PARTNER_ACTIONS.LOAD_W9}`);
          this.w9Status = this.$store.state.partner.w9.status;
        }

        this.isLoading = false;

        if (this.hasContractor) {
          if (this.hasAccess(CO_BRANDED_PAGE_ACCESS, CO_BRANDED_PAGE_ACCESS_MANAGE)) {
            if (this.contractorHasPage === true) {
              const options = { month: '2-digit', day: '2-digit', year: 'numeric' };
              const coBrandedAnalyticsData = await coBrandedAnalytics(
                this.contractorId,
                new Intl.DateTimeFormat('en-US', options).format(this.startDate),
                new Intl.DateTimeFormat('en-US', options).format(this.endDate),
              );
              this.coBrandedAnalytics = { ...coBrandedAnalyticsData.data };

              await this.updPages(this.contractorId);
              this.coBrandedWidgetIsLoading = false;
            } else {
              this.coBrandedWidgetIsLoading = false;
            }
          }
        }
      } catch (e) {
        this.$buefy.toast.open({
          message: 'Something wrong',
          type: 'is-danger',
          duration: 5000,
        });
      }
    },
    closeNotificationWithNews(newsId) {
      const array = JSON.parse(localStorage.getItem('newsAndUpdates'));
      const index = array.findIndex((object) => object.id === newsId);
      array[index].closed = 1;
      localStorage.setItem('newsAndUpdates', JSON.stringify(array));
    },
    makeCoBrandedWidgetCardForCustomerWithPage(originalCard) {
      let card = originalCard;
      const { submittedApplications } = this.coBrandedAnalytics;
      const hasReceivedOffers = this.coBrandedAnalytics.receivedOffers > 0;

      if (submittedApplications < 10 && hasReceivedOffers === false) {
        card = this.widgetCardData.coBrandedPage['2'];
      } else if (
        this.hasAccess(REPORTS_APPLICATIONS_ACCESS, REPORTS_APPLICATIONS_ACCESS_MANAGE)
        && (submittedApplications >= 10 || hasReceivedOffers === true)
      ) {
        card = this.widgetCardData.coBrandedPage['3'];
        card.stat.items = card.stat.getItems(this.coBrandedAnalytics);
        card.to.params.customStart = this.startDate;
        card.to.params.customEnd = this.endDate;
      }

      if (card) {
        const pages = [...this.contractorPages];
        pages.sort((a, b) => a.id - b.id);
        const links = pages.map((page) => ({
          name: page.pageName,
          value: page.fullUrl,
        }));
        card.copyLinkButton.links = links;
        if (Object.prototype.hasOwnProperty.call(card, 'subtitleLink') && links[0]) {
          card.subtitleLink.link = links[0].value;
        }

        card.isLoading = false;
      }

      return card;
    },
  },
};
</script>

<style lang="stylus" scoped>
.dashboard-loading {
  position: relative;
  height: 100vh;
}
.dashboard-greeting {
  & h1 {
    font-size: 34px;
    font-weight: 700;
    color: #4C7B93;
    margin: 0;
    @media (max-width 1170px) {
      font-size: 26px;
    }
    @media (max-width 360px) {
      line-height: 1.875rem;
    }
  }
  & p {
    font-size: 18px;
    font-weight: 400;
    line-height: 26px;
    color: #525F66;
    margin-top: 14px;
    @media (max-width 1170px) {
      font-size: 16px;
    }
  }
}
.dashboard-news-and-updates-notification {
  padding-top 18px
  .notification-wrapper.block:not(:last-child) {
    margin-bottom 12px
  }
}
.dashboard-widget-list {
  display: flex;
  flex-wrap: wrap;
  margin: 25px -15px -15px -15px;
  @media (max-width 1170px) {
    margin: 26px -14px -14px -14px;
  }
  @media (max-width 747px) {
    margin: 30px -10px -10px -10px;
  }
  & .widget-card {
    margin: 15px;
    @media (max-width 1170px) {
      margin: 14px;
    }
    @media (max-width 747px) {
      margin: 10px;
    }
  }
}

</style>
